import type {
  ProductTypeValue,
  PTRDataStoreBlankItem,
  PTRDataStoreType,
} from '@app/data-stores/ptr/types';
import type { DeepReadonly } from '@app/types';
import { PRODUCT_FAMILY_OPTION } from '../components/ProductConfigurator/utils';

export const FAMILY_OPTION_PREFIX = 'product_family_';
export const PRODUCT_OPTION_PREFIX = 'product_type_';

/**
 * Converts an options object to a canonical selector string.
 * Keys are sorted alphabetically to ensure consistent ordering.
 * @example { bodyColor: 'black', size: 'xl' } => 'bodyColor=black&size=xl'
 */
export const canonicaliseSelectedOptions = (
  options: Record<string, string>
): string => {
  const sortedEntries = Object.entries(options).sort((a, b) =>
    a[0].localeCompare(b[0])
  );
  return new URLSearchParams(sortedEntries).toString();
};

/**
 * Creates a new canonical string with one option value changed.
 * @example canonicaliseTweakedSelectedOptions({ color: 'red', size: 'xl' }, 'color', 'blue')
 *          => 'color=blue&size=xl'
 */
export const canonicaliseTweakedSelectedOptions = (
  options: Record<string, string>,
  optionKey: string,
  optionValueKey: string
): string => {
  const newOptions = { ...options, [optionKey]: optionValueKey };
  const entries = Object.entries(newOptions);
  const sorted = entries.sort(([ak], [bk]) => ak.localeCompare(bk));
  const keys = sorted.map(([k]) => k);
  if (new Set(keys).size !== keys.length) {
    throw new Error(`duplicate keys found in options: ${keys}`);
  }
  return new URLSearchParams(sorted).toString();
};

/**
 * Parses a canonical string back into an options object prefixed with either product_ or family_.
 * @example 'bodyColor=black&size=xl' => { product_bodyColor: 'black', product_size: 'xl' }
 */
export const prefixedOptionsCanonicalHash = (
  canonical?: string,
  entity?: string
): Record<string, string> => {
  const params = new URLSearchParams(canonical);
  const prefix =
    entity === PRODUCT_FAMILY_OPTION
      ? FAMILY_OPTION_PREFIX
      : PRODUCT_OPTION_PREFIX;
  return Object.fromEntries(
    Array.from(params).map(([key, value]) => [`${prefix}${key}`, value])
  );
};

/**
 * Parses a canonical string back into an options object.
 * @example 'bodyColor=black&size=xl' => { bodyColor: 'black', size: 'xl' }
 */
export const optionsCanonicalHash = (
  canonical?: string
): Record<string, string> => {
  const params = new URLSearchParams(canonical);
  return Object.fromEntries(params);
};

/**
 * Builds an index of blank items by their blankItemId.
 * @returns Record<blankItemId, BlankItem>
 */
export const buildBlankItemsById = (
  productTypes: PTRDataStoreType['productTypes']
): Record<string, PTRDataStoreBlankItem> => {
  if (!productTypes) return {};
  const index: Record<string, PTRDataStoreBlankItem> = {};
  for (const productType of Object.values(productTypes)) {
    if (!productType) continue;
    for (const blankItem of productType.blankItems) {
      if (!blankItem) continue;
      index[blankItem.blankItemId] = blankItem;
    }
  }
  return index;
};

/**
 * Builds a lookup table to find inventory items by product type and canonical options.
 * Structure: index[productTypeId][equivalentCanonical] -> InventoryItem
 *
 * Example:
 * {
 *   "aggro-clanger": {
 *     "bodyColor=black&printLocation=front&size=xs": InventoryItemObject,
 *     "bodyColor=charcoal_grey&printLocation=front&size=m": InventoryItemObject,
 *   },
 *   "aggro-emu": {
 *     "bodyColor=navy&printLocation=front&size=s": InventoryItemObject,
 *   }
 * }
 */
export const buildCanonicalInventoryIndex = <
  T extends { productTypeId: string; blankItemId: string },
>(
  items: T[],
  blankItemsById: Record<string, PTRDataStoreBlankItem>
): Record<string, Record<string, T>> => {
  const index: Record<string, Record<string, T>> = {};
  for (const item of items) {
    const blankItem = blankItemsById[item.blankItemId];
    // Skip items without a matching blank item in PTR data
    if (!blankItem) continue;

    if (!index[item.productTypeId]) {
      index[item.productTypeId] = {};
    }
    index[item.productTypeId][blankItem.options.equivalentCanonical] = item;
  }
  return index;
};

/**
 * Finds a ProductFamily by its productFamilyId from the productFamilies record.
 * If no family is found, creates a synthetic family from the productType.
 */
export const findProductFamilyById = (
  productTypes: PTRDataStoreType['productTypes'],
  productFamilyId: string,
  productFamilies?: PTRDataStoreType['productFamilies']
): PTRDataStoreType['productFamilies'][number] => {
  if (productFamilies) {
    const family = productFamilies[productFamilyId];
    if (family) return family;
  }
  const productType = productTypes[productFamilyId];

  // Create synthetic family for standalone product type
  return {
    productFamilyId: productType.productTypeId,
    productTypeIds: [productType.productTypeId],
    defaultProductTypeId: productType.productTypeId,
    lifecycleStage: productType.lifecycleStage,
    publicName: productType.copy?.publicName ?? {
      en: productType.productTypeId,
    },
    publicNamePlural: productType.copy?.publicName ?? {
      en: productType.productTypeId,
    },
    options: [],
    optionsOrder: [],
    warnOnOptionFallback: false,
  };
};

/**
 * Finds a ProductType by its productFamilyOptionSelections.equivalentCanonical
 * within a specific product family.
 */
export const findProductTypeByFamilyOptionSelector = (
  productTypes: PTRDataStoreType['productTypes'],
  productFamilyId: string,
  equivalentCanonical?: string
): DeepReadonly<ProductTypeValue> | null => {
  const pt = Object.values(productTypes).find(
    (pt) =>
      pt?.productFamilyId === productFamilyId &&
      pt?.productFamilyOptionSelections?.equivalentCanonical ===
        equivalentCanonical
  );
  return pt ? pt : null;
};

/**
 * Gets the relevant product families and product types from PTR data store from a given productTypeId.
 * If the product type belongs to a family, returns all product types in that family.
 * Otherwise, returns just the single product type.
 */
export const getProductFamiliesAndTypesFromPTID = (
  productTypes: PTRDataStoreType['productTypes'],
  productFamilies: PTRDataStoreType['productFamilies'],
  productTypeId: string,
  productFamilyId?: string | null
): {
  relevantProductTypes: PTRDataStoreType['productTypes'];
  relevantProductFamilies: PTRDataStoreType['productFamilies'];
} => {
  const productType = productTypes[productTypeId];
  const productFamily = productFamilyId
    ? productFamilies[productFamilyId]
    : undefined;

  // A caller can withhold a real family (`productFamilyId: null`, e.g.
  // PdpStylePicker's Control/off cohort) even though the product type's
  // own PTR record still has one (e.g. cactus-bizzo's "t-shirts"), which
  // would otherwise leak via its own fields. Only scrub when it actually
  // has a family to strip - a genuinely family-less type is unaffected.
  const relevantProductTypes: PTRDataStoreType['productTypes'] = productFamily
    ? Object.fromEntries(
        productFamily.productTypeIds
          .filter((id) => productTypes[id])
          .map((id) => [id, productTypes[id]])
      )
    : {
        [productTypeId]: productType?.productFamilyId
          ? {
              ...productType,
              productFamilyId: null,
              productFamilyOptionSelections: null,
            }
          : productType,
      };

  const relevantProductFamilies: PTRDataStoreType['productFamilies'] =
    productFamily ? { [productFamily.productFamilyId]: productFamily } : {};

  return {
    relevantProductTypes,
    relevantProductFamilies,
  };
};
